import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeEvery, getContext, put, call} from 'redux-saga/effects'
import {Dictionary, keyBy} from 'lodash'
import Device from '../../models/Device'
import Api, {UnauthorizedError} from 'Api'
import {actions as searchActions} from './search'
import {AlertMessageData} from 'components/AlertMessage'

export type DevicesState = {
  devices: Dictionary<Device>
  total: number
  currentPage: number
  loading: boolean
  checkingName: boolean
  error: string | undefined
  warning?: AlertMessageData
}
export const initialState: DevicesState = {
  devices: {},
  total: 0,
  currentPage: 1,
  loading: false,
  checkingName: false,
  error: undefined,
}

export const actions = {
  set: createAction<{devices: Device[], total?: number, page?: number}>('devices/set'),
  get: createAction('devices/get'),
  upsert: createAction<{device: Device}>('devices/upsert'),
  getNameByPatientId: createAction<{
    patientId: string
    onFound?: (name: string, udid: string) => void
  }>('devices/getNameByPatientId'),
  update: createAction<{device: Device}>('devices/update'),
  add: createAction<{device: Device; patientId: string}>('devices/add'),
  checkNameAvailability: createAction<{name: string; patientId: string}>(
    'devices/checkNameAvailability',
  ),
  delete: createAction<{id: string}>('devices/delete'),
  setLoading: createAction<{loading: boolean}>('devices/setLoading'),
  setCheckingName: createAction<{checkingName: boolean}>('devices/setCheckingName'),
  setError: createAction<{error?: string}>('devices/setError'),
  setWarning: createAction<{warning?: AlertMessageData}>('devices/setWarning'),
}

export const reducer = createReducer(initialState, {
  [actions.set.type]: (state, action: ReturnType<typeof actions.set>) => {
    const {devices, total, page} = action.payload
    return {
      ...state,
      devices: keyBy(devices, 'id'),
      ...(total !== undefined && {total}),
      ...(page !== undefined && {currentPage: page}),
    }
  },
  [actions.upsert.type]: (state, action: ReturnType<typeof actions.upsert>) => {
    const {device} = action.payload
    return {...state, devices: {...state.devices, [device.id]: device}}
  },
  [actions.setLoading.type]: (state, action: ReturnType<typeof actions.setLoading>) => {
    const {loading} = action.payload
    return {...state, loading}
  },
  [actions.setCheckingName.type]: (state, action: ReturnType<typeof actions.setCheckingName>) => {
    const {checkingName} = action.payload
    return {...state, checkingName}
  },
  [actions.setError.type]: (state, action: ReturnType<typeof actions.setError>) => {
    const {error} = action.payload
    return {...state, error}
  },
  [actions.setWarning.type]: (state, action: ReturnType<typeof actions.setWarning>) => {
    const {warning} = action.payload
    return {...state, warning}
  },
})

const getSaga = function*() {
  const api: Api = yield getContext('api')
  try {
    const {devices, total, page} = yield call(api.getDevicesPage, 1, 100)
    yield put(actions.set({devices, total, page}))
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) throw error
    yield put(actions.setError({error: error?.message ?? 'Error loading devices'}))
  }
}

const getNameByPatientIdSaga = function*(action: ReturnType<typeof actions.getNameByPatientId>) {
  const {patientId, onFound} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setCheckingName({checkingName: true}))
  try {
    const device: {name: string, udid: string} | undefined = yield call(
      api.getDeviceNameByPatientId,
      patientId,
    )
    if (device == null) {
      yield put(actions.setError({error: undefined}))
      return
    }
    if (onFound) {
      yield call(onFound, device.name, device.udid)
    }
    yield put(actions.setError({error: undefined}))
  } finally {
    yield put(actions.setCheckingName({checkingName: false}))
  }
}

const addSaga = function*(action: ReturnType<typeof actions.add>) {
  const {device, patientId} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))
  try {
    yield call(api.addDevice, device, patientId)
    yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) {
      // throw so it redirects the user to the login page
      throw error
    }
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

const deleteSaga = function*(action: ReturnType<typeof actions.delete>) {
  const {id} = action.payload
  const api: Api = yield getContext('api')
  yield call(api.deleteDevice, id)
  yield put(searchActions.refreshSearch())
}

const updateSaga = function*(action: ReturnType<typeof actions.update>) {
  const {device} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))

  try {
    const updated: Device = yield call(api.updateDevice, device)
    yield put(actions.upsert({device: updated}))
    yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    if (error === UnauthorizedError) {
      // throw so it redirects the user to the login page
      throw error
    }
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

const checkNameAvailabilitySaga = function*(
  action: ReturnType<typeof actions.checkNameAvailability>,
) {
  const {name, patientId} = action.payload
  if (!name || !patientId) {
    return
  }
  const api: Api = yield getContext('api')
  try {
    const patientIds = yield call(api.checkDeviceNameAvailability, name, patientId)
    if (!patientIds || patientIds.length === 0) {
      yield put(actions.setWarning({warning: undefined}))
      return
    }

    yield put(
      actions.setWarning({
        warning: {
          message: 'This device name is being used by more than one patient:',
          details: patientIds,
        },
      }),
    )
  } catch (error) {
    if (error === UnauthorizedError) {
      throw error
    }
    const message = (error as Error)?.message ?? 'Unable to verify device name availability.'
    yield put(actions.setError({error: message}))
  }
}

export const saga = function*() {
  yield takeEvery(actions.delete, deleteSaga)
  yield takeEvery(actions.get, getSaga)
  yield takeEvery(actions.getNameByPatientId, getNameByPatientIdSaga)
  yield takeEvery(actions.add, addSaga)
  yield takeEvery(actions.update, updateSaga)
  yield takeEvery(actions.checkNameAvailability, checkNameAvailabilitySaga)
}
